// src/lib/googleAuthNative.ts
//
// Native (Android/Capacitor) Google Sign-In via the system-browser OAuth
// redirect flow — reuses the exact same Supabase `signInWithOAuth` call the
// web app already makes; it does NOT use a native Google SDK / ID-token
// exchange (see docs/scorecards for why that alternative was rejected).
//
// Flow:
//   1. Ask Supabase for the OAuth authorize URL without letting the SDK try
//      to navigate `window.location` — there is no meaningful browser
//      location inside a Capacitor WebView — via `skipBrowserRedirect: true`.
//   2. Open that URL in the system browser (Chrome Custom Tabs on Android)
//      via `@capacitor/browser`. Google's OAuth policy blocks its login
//      pages from rendering inside an embedded WebView
//      (`disallowed_useragent`, enforced since 2023); Custom Tabs is a real
//      system-browser context, not an embedded WebView, so this is
//      unaffected — it's Google's own recommended workaround.
//   3. Google + Supabase complete the OAuth dance entirely in the browser,
//      then redirect to https://visaguideos.com/auth/callback — the same
//      redirect URL the web flow already uses (so it's already on
//      Supabase's allow-list, nothing new to add there) — which is covered
//      by the Android App Links intent-filter (autoVerify) configured in
//      AndroidManifest.xml.
//   4. Because App Links verification passed for this domain, Android hands
//      that URL to *this app* instead of rendering it in the browser,
//      delivered via `@capacitor/app`'s `appUrlOpen` event. That's a native
//      event, not a page load, so supabase-js's automatic
//      `detectSessionInUrl` parsing (which only runs when the GoTrueClient
//      is constructed / on real page load) never sees it — this module
//      manually extracts the token/code from the delivered URL and
//      finishes the exchange by hand.
//
// Untested end-to-end: the Google Cloud OAuth clients don't exist yet
// (Supabase's Google provider is disabled project-side), so nothing here
// has completed a real handshake. See the note at the bottom of this file
// for exactly what to do once credentials exist.

import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import type { URLOpenListenerEvent } from '@capacitor/app';
import { supabase } from '@/lib/supabase';

const CALLBACK_PATH = '/auth/callback';
// Must match a redirect URL already allow-listed in Supabase Auth settings.
// This is the same URL the web flow uses (`${window.location.origin}/auth/callback`
// in production), so no new entry is needed there.
const NATIVE_REDIRECT_TO = `https://visaguideos.com${CALLBACK_PATH}`;

export const isNativePlatform = () => Capacitor.isNativePlatform();

/**
 * Kicks off the native Google sign-in flow. Resolves once the system
 * browser has been opened for the user to complete sign-in — it does NOT
 * wait for sign-in to finish. The session is established asynchronously,
 * once the OS delivers the redirect back into the app, by whatever
 * `appUrlOpen` handler was registered via `registerNativeGoogleAuthListeners`.
 */
export async function signInWithGoogleNative(): Promise<void> {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: NATIVE_REDIRECT_TO,
      skipBrowserRedirect: true,
    },
  });

  if (error) throw error;
  if (!data?.url) {
    throw new Error('Supabase did not return an OAuth authorize URL');
  }

  await Browser.open({ url: data.url });
}

/**
 * Extracts a Supabase session from a deep-link URL delivered via
 * `appUrlOpen`, handling both flow types the JS client can be configured
 * for:
 *   - implicit (current default for this project — see src/lib/supabase.ts,
 *     which does not set `flowType`, and GoTrueClient's own default):
 *     ...auth/callback#access_token=xxx&refresh_token=yyy
 *   - PKCE (not currently used here, but handled defensively in case that
 *     ever changes): ...auth/callback?code=xxxx
 *
 * Returns true if the URL was recognized as our auth callback (regardless
 * of whether the exchange succeeded) — the caller uses this to decide
 * whether to close the in-app browser tab.
 */
async function handleAuthDeepLink(url: string): Promise<boolean> {
  if (!url.includes(CALLBACK_PATH)) return false;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  const query = parsed.searchParams;
  const fragment = new URLSearchParams(parsed.hash.replace(/^#/, ''));

  const oauthError = query.get('error') || fragment.get('error');
  if (oauthError) {
    console.error(
      'Google sign-in returned an error:',
      oauthError,
      query.get('error_description') || fragment.get('error_description'),
    );
    return true;
  }

  // PKCE flow
  const code = query.get('code');
  if (code) {
    const { error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) console.error('exchangeCodeForSession failed:', error.message);
    return true;
  }

  // Implicit flow (current default)
  const access_token = fragment.get('access_token');
  const refresh_token = fragment.get('refresh_token');
  if (access_token && refresh_token) {
    const { error } = await supabase.auth.setSession({ access_token, refresh_token });
    if (error) console.error('setSession failed:', error.message);
    return true;
  }

  // Matched the callback path but had neither shape — not a fatal case,
  // just nothing to do (e.g. a stray/duplicate deep-link delivery).
  return false;
}

/**
 * Registers the listeners that catch the OAuth redirect back into the app
 * and keep loading state honest if the user just closes the browser tab
 * without completing sign-in. Call once near the app root, native only.
 * Returns a cleanup function.
 */
export function registerNativeGoogleAuthListeners(callbacks: {
  /** Fired once a callback URL was matched and the session exchange was attempted. */
  onCallbackHandled: () => void;
  /** Fired whenever the in-app browser tab closes, including the one we close ourselves after success — keep this idempotent. */
  onBrowserClosed: () => void;
}): () => void {
  const pendingHandles: Promise<PluginListenerHandle>[] = [];

  pendingHandles.push(
    App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
      const handled = await handleAuthDeepLink(event.url);
      if (handled) {
        await Browser.close().catch(() => {});
        callbacks.onCallbackHandled();
      }
    }),
  );

  pendingHandles.push(
    Browser.addListener('browserFinished', () => {
      callbacks.onBrowserClosed();
    }),
  );

  return () => {
    pendingHandles.forEach((p) => {
      p.then((h) => h.remove()).catch(() => {});
    });
  };
}

// ─────────────────────────────────────────────────────────────────────────
// NEXT SESSION — once the Google Cloud OAuth credentials exist:
//
// 1. In Supabase Dashboard → Authentication → Providers → Google:
//    enable the provider, paste the Web OAuth client's Client ID + Client
//    Secret (NOT the Android client — Android has no client secret and
//    isn't used by this browser-redirect flow at all). This step needs the
//    Supabase Management API / dashboard access this worktree doesn't have.
// 2. Confirm `https://visaguideos.com/auth/callback` is in the Redirect URLs
//    allow-list (Authentication → URL Configuration) — it likely already is,
//    since the web flow uses the same URL.
// 3. First real test: build+sign a debug APK (`npm run cap:sync` then open
//    android/ in Android Studio, or `npx cap run android`), install on a
//    device/emulator with the debug keystore fingerprint already in
//    assetlinks.json, tap "Continue with Google" on /login, and confirm:
//      a. Chrome Custom Tab opens to Google's real consent screen (not an
//         embedded webview — if it renders inside the app itself instead of
//         a Custom Tab, something regressed).
//      b. After granting consent, Android hands control back to the app
//         (not the browser) — this is the actual proof the App Links
//         autoVerify + assetlinks.json fingerprint match is working, which
//         has never been attack-tested, only inspected.
//      c. `useAuth().user` populates and the app lands on /journey.
// 4. If App Links verification silently fails (step 3b lands in the browser
//    instead of the app), check `adb shell pm get-app-links com.visaguideos.app`
//    to see Android's own verification verdict — don't just assume the
//    manifest config was enough.
// ─────────────────────────────────────────────────────────────────────────
